// Google Identity Services (One Tap) throws from inside its own minified script
// (accounts.google.com/gsi/client) with no app code anywhere in the stack - noise we can't fix or
// attribute to our code. Observed as the single highest-volume frontend Sentry issue, spiking on
// Yandex Browser/Android, likely from its tracking protection interfering with Google's script.
//
// This filter was deployed once already (see git history) to strip Sentry's own xhr instrumentation
// frames before checking, but it never once fired in production despite the issue continuing at
// full volume (773 events/day across ANYKOD-FRONTEND-G0/G2 the day after that fix shipped).
// Reproduced live 2026-08-06 by hooking the real production SDK's `beforeSend` and forcing an
// error through the exact same capture path Sentry reports for every sampled event of this issue
// (mechanism `auto.browser.browserapierrors.xhr.onreadystatechange`, confirmed on 100% of 152
// sampled events across both issues): the previous check compared `frame.filename` against the
// literal string `/gsi/client` and excluded frames whose filename included `@sentry/`. Neither
// ever matches in a real browser:
//   - Sentry ships bundled with the app (one file, not split), so its own instrumentation frames
//     have `filename` equal to our own /build/assets/app-*.js bundle URL, never a `@sentry/...`
//     path - that only appears in Sentry's *server-side*, sourcemapped view of the stack (the
//     Sentry UI/API), which is not what `beforeSend` receives client-side.
//   - A real gsi/client frame's `filename` is the full `https://accounts.google.com/gsi/client`
//     URL, not the shortened `/gsi/client` string - that shortening is also a server-side,
//     display-only transform.
// So the exclusion filter matched nothing, the filename comparison matched nothing, and every
// event sailed through to Sentry.
//
// Fix: since Sentry's own bundled instrumentation frames are indistinguishable from real app code
// by filename in production, don't try to filter them out by name at all. Instead use
// `exceptionValue.mechanism.type` - set by the SDK itself when it wraps a call, so unaffected by
// minification or bundling - to recognise that a run of *leading* frames belongs to Sentry's own
// wrapper, and skip past it to find the first gsi/client frame. Everything from that point to the
// end of the stack still has to be gsi/client, so a real app frame anywhere else (including one
// sitting between our wrapper and gsi/client) still fails the check, same as before.
const GSI_CLIENT_URL = 'https://accounts.google.com/gsi/client';

// The full family of Sentry's BrowserApiErrors auto-instrumentation (xhr, fetch, setTimeout,
// setInterval, requestAnimationFrame, addEventListener/handleEvent) all report mechanism types
// under this prefix - see browserapierrors.js's `type: `auto.browser.browserapierrors.${...}``.
// Every sampled production event of this issue used the xhr.onreadystatechange variant, but the
// prefix (rather than the exact string) also covers the other wrapped entry points gsi/client
// could plausibly be reached through.
const INSTRUMENTATION_MECHANISM_PREFIX = 'auto.browser.browserapierrors.';

export function isThirdPartyGsiClientError(event) {
    const exceptionValues = event.exception?.values;
    if (!exceptionValues?.length) {
        return false;
    }

    return exceptionValues.every((exceptionValue) => {
        const frames = exceptionValue.stacktrace?.frames ?? [];
        const mechanismType = exceptionValue.mechanism?.type ?? '';
        const isInstrumentedCall = mechanismType.startsWith(INSTRUMENTATION_MECHANISM_PREFIX);

        // A plain (non-instrumented) capture, e.g. a bare unhandled promise rejection, adds no
        // synthetic frames of its own - every frame has to be gsi/client already, as before.
        // An instrumented call may be preceded by Sentry's own wrapper frame(s), which live in
        // our bundle and can't be told apart from app code by filename - skip forward to the
        // first gsi/client frame instead of trying to count/name them.
        const firstGsiFrameIndex = frames.findIndex((frame) => frame.filename === GSI_CLIENT_URL);
        const relevantFrames = isInstrumentedCall
            ? (firstGsiFrameIndex === -1 ? [] : frames.slice(firstGsiFrameIndex))
            : frames;

        return relevantFrames.length > 0 && relevantFrames.every((frame) => frame.filename === GSI_CLIENT_URL);
    });
}
